// src/components/ComptesRendus/CompteRenduDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Calendar, User, MapPin, FileText,
  Clock, CheckCircle, AlertCircle, Archive, Edit2, Save, X
} from 'lucide-react';
import Layout from '../Layout';

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;
const getToken = () => localStorage.getItem('access_token');

export default function CompteRenduDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [compteRendu, setCompteRendu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    resume: '',
    decisions: [],
    points_cles: []
  });
  const [newDecision, setNewDecision] = useState('');
  const [newPoint, setNewPoint] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateSuccess, setUpdateSuccess] = useState(false);

  useEffect(() => {
    fetchCompteRendu();
  }, [id]);

  const fetchCompteRendu = async () => {
    setLoading(true);
    try {
      const token = getToken();
      if (!token) {
        setError("Vous devez être connecté");
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/comptes-rendus/${id}/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error("Erreur lors du chargement");
      }

      const data = await response.json();
      setCompteRendu(data);

      setEditForm({
        resume: data.resume || '',
        decisions: data.decisions || [],
        points_cles: data.points_cles || []
      });
    } catch (error) {
      console.error("Erreur chargement:", error);
      setError(error.message || "Impossible de charger le compte rendu");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/comptes-rendus/${id}/download/?token=${token}`);

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `compte_rendu_${id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        alert("Erreur lors du téléchargement");
      }
    } catch (error) {
      console.error("Erreur téléchargement:", error);
      alert("Erreur lors du téléchargement");
    }
  };

  const handleUpdate = async () => {
    setUpdateLoading(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_URL}/comptes-rendus/${id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reunion: compteRendu.reunion,
          resume: editForm.resume,
          decisions: editForm.decisions,
          points_cles: editForm.points_cles,
          statut: compteRendu.statut
        })
      });

      if (!response.ok) {
        throw new Error("Erreur lors de la mise à jour");
      }

      const updatedData = await response.json();
      setCompteRendu(updatedData);
      setIsEditing(false);
      setUpdateSuccess(true);

      setTimeout(() => setUpdateSuccess(false), 3000);
    } catch (error) {
      console.error("Erreur mise à jour:", error);
      alert("Erreur lors de la mise à jour du compte rendu");
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleAddDecision = () => {
    if (newDecision.trim()) {
      setEditForm({
        ...editForm,
        decisions: [...editForm.decisions, newDecision.trim()]
      });
      setNewDecision('');
    }
  };

  const handleRemoveDecision = (index) => {
    setEditForm({
      ...editForm,
      decisions: editForm.decisions.filter((_, i) => i !== index)
    });
  };

  const handleAddPoint = () => {
    if (newPoint.trim()) {
      setEditForm({
        ...editForm,
        points_cles: [...editForm.points_cles, newPoint.trim()]
      });
      setNewPoint('');
    }
  };

  const handleRemovePoint = (index) => {
    setEditForm({
      ...editForm,
      points_cles: editForm.points_cles.filter((_, i) => i !== index)
    });
  };

  const getStatusStyle = (statut) => {
    const styles = {
      brouillon: {
        bg: '#f3f4f6',
        color: '#4b5563',
        icon: <Clock size={14} />,
        text: 'Brouillon'
      },
      publie: {
        bg: '#dbeafe',
        color: '#2563eb',
        icon: <CheckCircle size={14} />,
        text: 'Publié'
      },
      archive: {
        bg: '#e5e7eb',
        color: '#6b7280',
        icon: <Archive size={14} />,
        text: 'Archivé'
      }
    };
    return styles[statut] || styles.publie;
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Chargement du compte rendu...</p>
        </div>
      </Layout>
    );
  }

  if (error || !compteRendu) {
    return (
      <Layout>
        <div style={styles.errorContainer}>
          <AlertCircle size={48} color="#ef4444" />
          <p style={{ color: "#ef4444", marginTop: 16 }}>{error || "Compte rendu non trouvé"}</p>
          <button onClick={() => navigate('/comptes-rendus')} style={styles.retryButton}>
            Retour à la liste
          </button>
        </div>
      </Layout>
    );
  }

  const status = getStatusStyle(compteRendu.statut);

  return (
    <Layout>
      <div style={styles.page}>
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>DÉTAIL DU COMPTE RENDU</h1>
            <p style={styles.subtitle}>
              {compteRendu.reunion_titre} • {new Date(compteRendu.created_at).toLocaleDateString('fr-FR')}
            </p>
          </div>
          <div style={styles.headerButtons}>
            <button onClick={() => navigate('/comptes-rendus')} style={styles.backButton}>
              <ArrowLeft size={18} />
              Retour
            </button>

            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                style={styles.editButton}
              >
                <Edit2 size={18} />
                Modifier
              </button>
            )}

            <button onClick={handleDownload} style={styles.downloadButton}>
              <Download size={18} />
              Télécharger PDF
            </button>
          </div>
        </div>

        {updateSuccess && (
          <div style={styles.successMessage}>
            <CheckCircle size={20} color="#10b981" />
            <span>Compte rendu mis à jour avec succès !</span>
          </div>
        )}

        <div style={styles.content}>
          <div style={styles.documentHeader}>
            <div style={styles.documentTitle}>
              <FileText size={32} color="#5e17eb" />
              <h2>COMPTE RENDU DE RÉUNION</h2>
            </div>
            <span style={{
              ...styles.statusBadge,
              backgroundColor: status.bg,
              color: status.color
            }}>
              {status.icon}
              {status.text}
            </span>
          </div>

          <div style={styles.infoGrid}>
            <div style={styles.infoCard}>
              <Calendar size={18} color="#5e17eb" />
              <div>
                <span style={styles.infoLabel}>Date</span>
                <span style={styles.infoValue}>
                  {new Date(compteRendu.created_at).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </span>
              </div>
            </div>

            <div style={styles.infoCard}>
              <User size={18} color="#5e17eb" />
              <div>
                <span style={styles.infoLabel}>Auteur</span>
                <span style={styles.infoValue}>{compteRendu.auteur_nom || 'Non spécifié'}</span>
              </div>
            </div>

            <div style={styles.infoCard}>
              <MapPin size={18} color="#5e17eb" />
              <div>
                <span style={styles.infoLabel}>Salle</span>
                <span style={styles.infoValue}>{compteRendu.reunion?.salle || 'Non spécifiée'}</span>
              </div>
            </div>
          </div>

          {/* Résumé - toujours affiché */}
          {isEditing ? (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>RÉSUMÉ</h3>
              <textarea
                value={editForm.resume}
                onChange={(e) => setEditForm({ ...editForm, resume: e.target.value })}
                style={styles.textarea}
                rows={12}
              />
            </div>
          ) : (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>RÉSUMÉ</h3>
              <div style={styles.resumeBox}>
                {compteRendu.resume.split('\n').map((line, i) => (
                  <p key={i} style={styles.paragraph}>{line || <br />}</p>
                ))}
              </div>
            </div>
          )}

          {/* Décisions - UNIQUEMENT en mode édition */}
          {isEditing && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>DÉCISIONS (optionnel)</h3>
              <div style={styles.listBox}>
                <p style={styles.hintText}>Les décisions sont optionnelles. Elles peuvent être intégrées directement dans le résumé.</p>
                {editForm.decisions.map((decision, i) => (
                  <div key={i} style={styles.editableListItem}>
                    <span style={styles.bullet}>•</span>
                    <span style={{ flex: 1 }}>{decision}</span>
                    <button
                      onClick={() => handleRemoveDecision(i)}
                      style={styles.removeButton}
                    >
                      <X size={16} color="#ef4444" />
                    </button>
                  </div>
                ))}

                <div style={styles.addItemContainer}>
                  <input
                    type="text"
                    value={newDecision}
                    onChange={(e) => setNewDecision(e.target.value)}
                    placeholder="Nouvelle décision..."
                    style={styles.addInput}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddDecision()}
                  />
                  <button
                    onClick={handleAddDecision}
                    style={styles.addButton}
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Points clés - UNIQUEMENT en mode édition */}
          {isEditing && (
            <div style={styles.section}>
              <h3 style={styles.sectionTitle}>POINTS CLÉS (optionnel)</h3>
              <div style={styles.listBox}>
                <p style={styles.hintText}>Les points clés sont optionnels. Ils peuvent être intégrés directement dans le résumé.</p>
                {editForm.points_cles.map((point, i) => (
                  <div key={i} style={styles.editableListItem}>
                    <span style={styles.bullet}>•</span>
                    <span style={{ flex: 1 }}>{point}</span>
                    <button
                      onClick={() => handleRemovePoint(i)}
                      style={styles.removeButton}
                    >
                      <X size={16} color="#ef4444" />
                    </button>
                  </div>
                ))}

                <div style={styles.addItemContainer}>
                  <input
                    type="text"
                    value={newPoint}
                    onChange={(e) => setNewPoint(e.target.value)}
                    placeholder="Nouveau point clé..."
                    style={styles.addInput}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddPoint()}
                  />
                  <button
                    onClick={handleAddPoint}
                    style={styles.addButton}
                  >
                    Ajouter
                  </button>
                </div>
              </div>
            </div>
          )}

          {isEditing && (
            <div style={styles.editActions}>
              <button
                onClick={() => setIsEditing(false)}
                style={styles.cancelButton}
                disabled={updateLoading}
              >
                <X size={18} />
                Annuler
              </button>
              <button
                onClick={handleUpdate}
                style={styles.saveButton}
                disabled={updateLoading}
              >
                {updateLoading ? (
                  <>
                    <div style={styles.smallSpinner}></div>
                    Mise à jour...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          )}

          <div style={styles.footer}>
            <p>Document généré par PolyMeet - {new Date().toLocaleDateString('fr-FR')}</p>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const styles = {
  page: {
    padding: "24px",
    background: "#f7f6ff",
    minHeight: "100vh",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  smallSpinner: {
    width: "16px",
    height: "16px",
    border: "2px solid rgba(255,255,255,0.3)",
    borderTop: "2px solid white",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    marginRight: "8px"
  },
  errorContainer: {
    textAlign: "center",
    padding: "60px",
    background: "white",
    borderRadius: "12px",
    maxWidth: "400px",
    margin: "100px auto"
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    background: "#5e17eb",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px"
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px'
  },
  title: {
    fontSize: '26px',
    color: '#1f1f2e',
    marginBottom: '4px',
    fontWeight: '500',
  },
  subtitle: {
    color: '#666',
    fontSize: '14px',
  },
  headerButtons: {
    display: 'flex',
    gap: '12px'
  },
  backButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 16px',
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#666',
    cursor: 'pointer',
  },
  editButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 20px',
    background: '#fbbf24',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#1f1f2e',
    cursor: 'pointer',
  },
  downloadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 20px',
    background: '#5e17eb',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'white',
    cursor: 'pointer',
  },
  successMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 20px',
    background: '#d1fae5',
    border: '1px solid #a7f3d0',
    borderRadius: '8px',
    marginBottom: '20px',
    color: '#065f46'
  },
  content: {
    background: 'white',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
  },
  documentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '32px',
    paddingBottom: '20px',
    borderBottom: '2px solid #5e17eb'
  },
  documentTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '500'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '32px',
    background: '#f8f7ff',
    padding: '20px',
    borderRadius: '12px'
  },
  infoCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  infoLabel: {
    display: 'block',
    fontSize: '12px',
    color: '#666',
    marginBottom: '2px'
  },
  infoValue: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#1f1f2e'
  },
  section: {
    marginBottom: '32px'
  },
  sectionTitle: {
    fontSize: '18px',
    color: '#5e17eb',
    marginBottom: '16px',
    fontWeight: '600'
  },
  resumeBox: {
    background: '#f9f9ff',
    padding: '20px',
    borderRadius: '12px',
    lineHeight: '1.8'
  },
  paragraph: {
    margin: '0 0 8px 0',
    color: '#333'
  },
  hintText: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
    fontStyle: 'italic'
  },
  listBox: {
    background: '#f9f9ff',
    padding: '20px',
    borderRadius: '12px'
  },
  listItem: {
    display: 'flex',
    gap: '12px',
    marginBottom: '10px',
    color: '#4b5563',
    lineHeight: '1.6'
  },
  editableListItem: {
    display: 'flex',
    gap: '12px',
    marginBottom: '12px',
    color: '#4b5563',
    lineHeight: '1.6',
    padding: '8px',
    background: 'white',
    borderRadius: '6px',
    border: '1px solid #e5e7eb'
  },
  bullet: {
    color: '#5e17eb',
    fontWeight: 'bold'
  },
  removeButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
  },
  addItemContainer: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px'
  },
  addInput: {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    fontSize: '14px',
    outline: 'none',
  },
  addButton: {
    padding: '10px 20px',
    background: '#5e17eb',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  textarea: {
    width: '100%',
    padding: '16px',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    lineHeight: '1.6',
    fontFamily: 'inherit',
    resize: 'vertical',
    outline: 'none',
  },
  editActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    marginTop: '24px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb'
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    fontSize: '14px',
    color: '#666',
    cursor: 'pointer',
  },
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 24px',
    background: '#10b981',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    color: 'white',
    cursor: 'pointer',
  },
  footer: {
    marginTop: '40px',
    paddingTop: '20px',
    borderTop: '1px solid #e5e7eb',
    textAlign: 'right',
    fontSize: '12px',
    color: '#9ca3af'
  }
};

const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);